import {useState} from 'react';
import {ChevronDown, ChevronRight, History, RefreshCw} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {useScrapyCollectionHistory} from '@/api/hooks';

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  spider_name: 'Spider',
  query: 'Query',
  spider_args_extra: 'Extra args',
  cron_expression: 'Schedule',
  is_active: 'Active',
  affiliate: 'Customer',
  policy_violation: 'Policy Violation',
  language: 'Language'
};

export function CollectionHistoryPanel({collectionId}: {readonly collectionId: string}) {
  const [collapsed, setCollapsed] = useState(true);
  const {
    data: rawHistory,
    isLoading,
    isFetching,
    refetch
  } = useScrapyCollectionHistory(collectionId, !collapsed);

  // An edit always writes a "deleted" row for the superseded version alongside a "created" row for
  // its replacement, in the same request - bookkeeping for Job.ScrapyCollectionId snapshot
  // stability, not a real deletion, and it never carries its own changes (the paired "created"
  // row's diff already shows what changed). ChangedAt defaults to Postgres `now()`
  // (InvestigationsDbContext), which is transaction-time, so both rows of a pair land on the exact
  // same timestamp; requiring changed_by to match too narrows a same-instant coincidence further,
  // to two DIFFERENT analysts independently deleting and creating at the identical microsecond.
  // Not a proof (the DTO doesn't expose the row id an edit's pair actually shares), just a heuristic
  // - a real standalone deletion (the DELETE endpoint records its own "deleted" row, same as an
  // edit's) still shows unless it collides with someone else's edit that precisely.
  const createdSignatures = new Set(
    (rawHistory ?? [])
      .filter((e) => e.audit_type === 'created')
      .map((e) => `${e.changed_at}|${e.changed_by}`)
  );
  const history = (rawHistory ?? []).filter(
    (entry) =>
      entry.audit_type !== 'deleted' ||
      !createdSignatures.has(`${entry.changed_at}|${entry.changed_by}`)
  );

  return (
    <div className="space-y-3 p-4">
      <div className="flex w-full items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={!collapsed}
          className="-ml-2.5"
          onClick={() => {
            setCollapsed((previous) => !previous);
          }}>
          {collapsed ? (
            <ChevronRight aria-hidden className="text-muted-foreground" />
          ) : (
            <ChevronDown aria-hidden className="text-muted-foreground" />
          )}
          <h4 className="text-foreground text-xs font-medium">Edit history</h4>
        </Button>
        {collapsed ? null : (
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={isFetching}
            onClick={() => {
              void refetch();
            }}>
            {isFetching ? <Spinner /> : <RefreshCw aria-hidden />}
            Refresh
          </Button>
        )}
      </div>

      {collapsed ? null : isLoading ? (
        <p className="text-muted-foreground flex items-center gap-2 text-xs">
          <Spinner aria-label="Loading history" /> Loading history…
        </p>
      ) : history.length === 0 ? (
        <Empty className="py-4">
          <EmptyHeader>
            <History aria-hidden className="text-muted-foreground size-4" />
            <EmptyTitle>No history recorded yet</EmptyTitle>
            <EmptyDescription>Edits to this collection will be listed here.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="space-y-2">
          {history.map((entry) => (
            <div key={entry.id} className="border-border rounded-lg border p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {/* Every surviving row is audit_type "created" - even a later edit's replacement
                      row, since an edit is "insert a new row" under the hood. The group's very first
                      row is the only one with no diff; every other "created" row IS an edit, so label
                      it as one rather than surfacing the storage-level term literally. */}
                  <Badge variant="outline">
                    {Object.keys(entry.changes).length > 0 ? 'updated' : entry.audit_type}
                  </Badge>
                  <span className="text-foreground font-medium">{entry.changed_by}</span>
                </div>
                <span className="text-muted-foreground text-xs">
                  {new Date(entry.changed_at).toLocaleString()}
                </span>
              </div>
              {Object.keys(entry.changes).length > 0 ? (
                <ul className="text-muted-foreground mt-2 space-y-1">
                  {Object.entries(entry.changes).map(([field, change]) => (
                    <li key={field}>
                      <span className="text-foreground font-medium">
                        {FIELD_LABELS[field] ?? field}:
                      </span>{' '}
                      <span className="font-mono">{change.old ?? '—'}</span>
                      {' → '}
                      <span className="font-mono">{change.new ?? '—'}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
